import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { trackEvent, trackPageView } from '@shared/lib/analytics'

const AFFILIATE_HOST = /(^|\.)(amazon\.[a-z.]+|amzn\.to)$/i
const ENGAGED_MS = 60_000          // active reading time that marks a genuine read
const ENGAGED_SCROLL = 75          // % of the article scrolled
const SCROLL_MILESTONES = [25, 50, 75, 100]

function articleSlug(pathname: string): string | undefined {
  return pathname.match(/^(?:\/(?:fr|es))?\/\d{4}\/\d{2}\/\d{2}\/([^/]+)\/?$/)?.[1]
}

function linkText(anchor: HTMLAnchorElement): string {
  return (anchor.textContent || anchor.getAttribute('aria-label') || anchor.title || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100)
}

/**
 * Single source of GA4 instrumentation, shared verbatim by both sites:
 *   - page_view on every SPA route change
 *   - affiliate_click / related_post_click / internal_content_click
 *   - reading engagement per article: real *active* time (paused while the tab
 *     is hidden) and real *max scroll depth*, emitted as scroll_depth milestones,
 *     an engaged_reader flag (>=60s active AND >=75% scrolled), and a final
 *     read_time carrying the measured seconds + depth on exit.
 */
export function AnalyticsTracker() {
  const location = useLocation()

  // ── page_view ──
  useEffect(() => {
    // Route components set their title/canonical in their own effects; defer one
    // tick so the page_view records the resolved title, not the previous page's.
    const timer = window.setTimeout(() => {
      trackPageView(`${location.pathname}${location.search}`)
    }, 0)
    return () => window.clearTimeout(timer)
  }, [location.pathname, location.search])

  // ── outbound / affiliate / internal link clicks ──
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return
      const anchor = event.target.closest<HTMLAnchorElement>('a[href]')
      if (!anchor) return

      let destination: URL
      try {
        destination = new URL(anchor.href, window.location.href)
      } catch {
        return
      }

      const common = {
        link_url: destination.href,
        link_domain: destination.hostname,
        link_text: linkText(anchor),
        page_path: window.location.pathname,
        article_slug: articleSlug(window.location.pathname),
      }

      if (AFFILIATE_HOST.test(destination.hostname)) {
        trackEvent('affiliate_click', {
          ...common,
          affiliate_name: 'Amazon',
          link_position: anchor.closest('.amz-panel, .amz-card, .product-panel') ? 'product_panel' : 'article_body',
          transport_type: 'beacon',
        })
        return
      }

      if (anchor.dataset.analytics === 'related-post' || anchor.closest('[data-analytics="related-post"], .related-posts')) {
        trackEvent('related_post_click', common)
        return
      }

      if (destination.origin === window.location.origin && anchor.closest('.post-content')) {
        trackEvent('internal_content_click', common)
      }
    }

    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  // ── reading engagement (article routes only) ──
  useEffect(() => {
    const slug = articleSlug(location.pathname)
    if (!slug) return
    const pagePath = `${location.pathname}${location.search}`

    let activeMs = 0
    let lastStart: number | null = document.visibilityState === 'visible' ? performance.now() : null
    let maxDepth = 0
    let engagedSent = false
    let readTimeSent = false
    const firedDepths = new Set<number>()

    const activeMsNow = () => activeMs + (lastStart != null ? performance.now() - lastStart : 0)
    const pause = () => {
      if (lastStart != null) {
        activeMs += performance.now() - lastStart
        lastStart = null
      }
    }
    const resume = () => {
      if (lastStart == null && document.visibilityState === 'visible') lastStart = performance.now()
    }

    const maybeEngaged = () => {
      if (engagedSent || activeMsNow() < ENGAGED_MS || maxDepth < ENGAGED_SCROLL) return
      engagedSent = true
      trackEvent('engaged_reader', {
        engagement_time_msec: Math.round(activeMsNow()),
        scroll_percent: Math.round(maxDepth),
        article_slug: slug,
        page_path: pagePath,
      })
    }

    const measureDepth = () => {
      const el = document.documentElement
      const scrollable = el.scrollHeight - el.clientHeight
      const depth = scrollable > 0 ? (el.scrollTop / scrollable) * 100 : 100
      if (depth > maxDepth) maxDepth = Math.min(100, depth)
      for (const milestone of SCROLL_MILESTONES) {
        if (maxDepth >= milestone && !firedDepths.has(milestone)) {
          firedDepths.add(milestone)
          trackEvent('scroll_depth', { percent: milestone, article_slug: slug, page_path: pagePath })
        }
      }
      maybeEngaged()
    }

    const flushReadTime = () => {
      if (readTimeSent) return
      readTimeSent = true
      pause()
      const seconds = Math.round(activeMs / 1000)
      if (seconds < 3) return // ignore bounces, prefetches, and prerender hits
      trackEvent('read_time', {
        engagement_time_msec: Math.max(1, Math.round(activeMs)),
        active_seconds: seconds,
        max_scroll_percent: Math.round(maxDepth),
        reached_end: maxDepth >= 90,
        article_slug: slug,
        page_path: pagePath,
        transport_type: 'beacon',
      })
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        pause()
        flushReadTime()
      } else {
        resume()
      }
    }

    const ticker = window.setInterval(maybeEngaged, 2_000)
    window.addEventListener('scroll', measureDepth, { passive: true })
    window.addEventListener('resize', measureDepth)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', flushReadTime)
    measureDepth()

    return () => {
      window.clearInterval(ticker)
      window.removeEventListener('scroll', measureDepth)
      window.removeEventListener('resize', measureDepth)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', flushReadTime)
      flushReadTime() // client-side navigation away from this article
    }
  }, [location.pathname, location.search])

  return null
}
